import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  Button,
  Input,
  Select,
  Table,
  Tag,
  Space,
  message,
  Spin,
  Empty,
  Pagination
} from 'antd'
import {
  PlusOutlined,
  SearchOutlined,
  TeamOutlined,
  EyeOutlined,
  RightOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import { useAuth } from '../hooks/AuthContext'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'

/* 對齊 MembershipInfoPage / MembershipTierSettingsPage 字體、按鈕圓角等 */
const GroupsContainer = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;

  .ant-btn {
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
  }

  .ant-input,
  .ant-select-selector {
    border-radius: 8px;
  }

  .ant-tag {
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
  }

  .ant-table {
    font-size: 14px;
  }

  .ant-table-thead > tr > th {
    font-weight: 600;
    color: #1a1a1a;
  }

  .ant-pagination-item {
    border-radius: 6px;
  }

  @media (max-width: 768px) {
    .ant-btn-sm {
      font-size: 13px;
    }

    .ant-input-sm,
    .ant-select-sm .ant-select-selector {
      border-radius: 8px;
    }
  }
`


const GroupsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;

  .header-left {
    display: flex;
    align-items: center;
  }

  .header-icon {
    font-size: 32px;
    color: #d5728a;
    margin-right: 16px;
  }

  h2 {
     margin: 0;
  font-size: 28px;
  font-weight: 300;
  color: #1a1a1a;
  letter-spacing: 0;
  }

  .ant-btn-primary {
    height: 38px;
    padding: 0 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 20px;

    h2 {
      font-size: 20px;
    }

    .header-icon {
      font-size: 28px;
      margin-right: 12px;
    }

    .ant-btn-primary {
      height: 32px;
      padding: 0 12px;
      font-size: 13px;
    }
  }
`

const SearchCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: none;
  overflow: hidden;

  .ant-card-body {
    padding: 24px;
  }

  .ant-input {
    font-size: 14px;
  }

  .ant-select-selector {
    font-size: 14px;
  }

  @media (max-width: 768px) {
    margin-bottom: 16px;
    border-radius: 12px;

    .ant-card-body {
      padding: 16px;
    }

    .ant-input-sm,
    .ant-select-sm .ant-select-selector {
      font-size: 13px;
    }
  }
`

const TableCard = styled(Card)`
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: none;
  overflow: hidden;

  .ant-card-body {
    padding: 0;
  }

  .ant-table {
    font-size: 14px;
  }

  .ant-empty-description {
    font-size: 14px;
    color: #8c8c8c;
  }

  @media (max-width: 768px) {
    border-radius: 12px;

    .ant-card-body {
      padding: 12px 14px;
    }
  }
`

/* 手機版群組卡片：緊湊、可點擊，字體/圓角與設計系統一致 */
const MobileCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 2px 0;
`

const MobileGroupCard = styled.div`
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:active {
    background: #f5f5f7;
  }
`

const MobileCardLeft = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const MobileCardName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
  display: flex;
  align-items: center;
  gap: 6px;
`

const MobileCardMeta = styled.div`
  font-size: 12px;
  color: #6e6e73;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const MobileCardIcon = styled.div`
  color: #86868b;
  font-size: 14px;
  flex-shrink: 0;
`

const PaginationWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;

  .ant-pagination-item {
    border-radius: 6px;
  }

  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    border-radius: 6px;
  }
`

const RELATIONSHIP_LABELS = {
  family: '家庭',
  company: '公司',
  friends: '朋友',
  other: '其他'
}

const MOBILE_PAGE_SIZE = 10

const CustomerGroupsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [loading, setLoading] = useState(false)
  const [groups, setGroups] = useState([])
  const [search, setSearch] = useState('')
  const [relationship, setRelationship] = useState(undefined)
  const [isActive, setIsActive] = useState(undefined)
  const [mobilePage, setMobilePage] = useState(1)

  const businessId = user?.businessId

  const fetchGroups = useCallback(async () => {
    if (!businessId) return
    setLoading(true)
    try {
      const params = {}
      if (search?.trim()) params.search = search.trim()
      if (relationship) params.relationship = relationship
      if (isActive !== undefined && isActive !== null) params.isActive = isActive

      const res = await api.get(
        `/api/business_B/${businessId}/customers/groups`,
        { params }
      )
      const data = res.data?.data ?? res.data ?? []
      setGroups(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('取得顧客群組失敗:', err)
      message.error(err?.response?.data?.message || '取得顧客群組失敗，請稍後再試')
      setGroups([])
    } finally {
      setLoading(false)
    }
  }, [businessId, search, relationship, isActive])

  useEffect(() => {
    fetchGroups()
  }, [fetchGroups])

  useEffect(() => {
    setMobilePage(1)
  }, [search, relationship, isActive])

  const columns = [
    {
      title: '群組名稱',
      dataIndex: 'name',
      key: 'name',
      width: 180,
        render: (name, record) => (
        <Space size={8}>
          <TeamOutlined style={{ color: '#d5728a', fontSize: 16 }} />
          <span style={{ fontWeight: 600, color: '#1d1d1f', fontSize: 14 }}>{name || '未命名群組'}</span>
          {record.isActive === false && (
            <Tag color='default'>已停用</Tag>
          )}
        </Space>
      )
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (v) => v || '-'
    },
    {
      title: '關係類型',
      dataIndex: 'relationship',
      key: 'relationship',
      width: 100,
      render: (v) => (
        <Tag color='blue'>{RELATIONSHIP_LABELS[v] ?? v ?? '其他'}</Tag>
      )
    },
    {
      title: '成員數',
      dataIndex: 'memberCount',
      key: 'memberCount',
      width: 90,
      align: 'center',
      render: (v) => v ?? 0
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type='link'
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/customers/groups/${record._id}`)
          }}
          style={{ fontSize: 14, fontWeight: 500, padding: 0 }}
        >
          查看
        </Button>
      )
    }
  ]

  const visibleGroups = isMobile
    ? groups.slice((mobilePage - 1) * MOBILE_PAGE_SIZE, mobilePage * MOBILE_PAGE_SIZE)
    : groups

  return (
    <GroupsContainer>
      <GroupsHeader>
        <div className='header-left'>
          <h2>顧客群組</h2>
        </div>
        <Button
          type='primary'
          size={isMobile ? 'small' : 'middle'}
          icon={<PlusOutlined />}
          onClick={() => navigate('/customers/groups/new')}
        >
          新增群組
        </Button>
      </GroupsHeader>

      <SearchCard>
        {isMobile ? (
          <Space direction='vertical' size={8} style={{ width: '100%' }}>
            <Input
              placeholder='搜尋群組名稱或描述'
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => fetchGroups()}
              size='small'
            />
            <Space wrap size={8}>
              <Select
                placeholder='關係類型'
                allowClear
                value={relationship}
                onChange={setRelationship}
                size='small'
                style={{ minWidth: 100 }}
              >
                {Object.entries(RELATIONSHIP_LABELS).map(([k, v]) => (
                  <Select.Option key={k} value={k}>{v}</Select.Option>
                ))}
              </Select>
              <Select
                placeholder='狀態'
                allowClear
                value={isActive}
                onChange={setIsActive}
                size='small'
                style={{ minWidth: 90 }}
              >
                <Select.Option value={true}>啟用</Select.Option>
                <Select.Option value={false}>停用</Select.Option>
              </Select>
              <Button type='primary' size='small' onClick={fetchGroups} loading={loading}>
                搜尋
              </Button>
            </Space>
          </Space>
        ) : (
          <Space wrap size='middle'>
            <Input
              placeholder='搜尋群組名稱或描述'
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => fetchGroups()}
              style={{ width: 240 }}
            />
            <Select
              placeholder='關係類型'
              allowClear
              value={relationship}
              onChange={setRelationship}
              style={{ width: 120 }}
            >
              {Object.entries(RELATIONSHIP_LABELS).map(([k, v]) => (
                <Select.Option key={k} value={k}>{v}</Select.Option>
              ))}
            </Select>
            <Select
              placeholder='啟用狀態'
              allowClear
              value={isActive}
              onChange={setIsActive}
              style={{ width: 120 }}
            >
              <Select.Option value={true}>啟用</Select.Option>
              <Select.Option value={false}>停用</Select.Option>
            </Select>
            <Button type='primary' onClick={fetchGroups} loading={loading}>
              搜尋
            </Button>
          </Space>
        )}
      </SearchCard>

      <TableCard>
        <Spin spinning={loading}>
          {isMobile ? (
            <>
              {groups.length === 0 ? (
                <Empty description='尚無顧客群組' image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <>
                  <MobileCardList>
                    {visibleGroups.map((g) => (
                      <MobileGroupCard key={g._id} onClick={() => navigate(`/customers/groups/${g._id}`)}>
                        <MobileCardLeft>
                          <MobileCardName>
                            <TeamOutlined style={{ color: '#d5728a', flexShrink: 0, fontSize: 16 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name || '未命名群組'}</span>
                            {g.isActive === false && <Tag color='default' style={{ margin: 0, flexShrink: 0 }}>已停用</Tag>}
                          </MobileCardName>
                          <MobileCardMeta>
                            <Tag color='blue' style={{ margin: 0, borderRadius: 6 }}>{RELATIONSHIP_LABELS[g.relationship] ?? g.relationship ?? '其他'}</Tag>
                            <span>{g.memberCount ?? 0} 人</span>
                            {g.description && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{g.description}</span>}
                          </MobileCardMeta>
                        </MobileCardLeft>
                        <MobileCardIcon>
                          <RightOutlined />
                        </MobileCardIcon>
                      </MobileGroupCard>
                    ))}
                  </MobileCardList>
                  {groups.length > MOBILE_PAGE_SIZE && (
                    <PaginationWrap>
                      <Pagination
                        current={mobilePage}
                        pageSize={MOBILE_PAGE_SIZE}
                        total={groups.length}
                        onChange={setMobilePage}
                        size='small'
                        simple
                        showSizeChanger={false}
                      />
                    </PaginationWrap>
                  )}
                </>
              )}
            </>
          ) : (
            <Table
              rowKey='_id'
              columns={columns}
              dataSource={groups}
              pagination={{
                showSizeChanger: true,
                showTotal: (t) => `共 ${t} 筆`,
                pageSizeOptions: ['10', '20', '50']
              }}
              locale={{
                emptyText: (
                  <Empty
                    description='尚無顧客群組'
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )
              }}
              onRow={(record) => ({
                onClick: () => navigate(`/customers/groups/${record._id}`),
                style: { cursor: 'pointer' }
              })}
            />
          )}
        </Spin>
      </TableCard>
    </GroupsContainer>
  )
}

export default CustomerGroupsPage
